import { ThreadListPanel } from "@/app/Threads/Components/ThreadListPanel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PlusIcon, TruckIcon, UserIcon } from "@heroicons/react/24/outline";
import { useThreads } from "@/app/Threads/hooks/useThreads";
import { useThreadSort } from "@/app/Threads/hooks/useThreadSort";
import { useCreateThread } from "@/app/Threads/hooks/useCreateThread";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { useState } from "react";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { CreateWorkOrderThreadDialog } from "@/app/Threads/Components/CreateWorkOrderThreadDialog";
import { useWorkOrderRecord } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderRecord";
import { useDebouncedValue } from "@/lib/Hooks/useDebouncedValue";
import { ModelType } from "@/gql/graphql";

export function WorkOrderAssignmentThreads({
  workOrderId,
  selectedId,
}: {
  workOrderId: string;
  selectedId: string | null;
}) {
  const { isAdmin } = useTenant();
  const { supplierId } = useGlobalSupplier();
  const workOrder = useWorkOrderRecord();
  const navigate = useNavigate();
  const { mutate: createThread, isPending: isCreatingThread } =
    useCreateThread();
  const { openAsPromise: openCreateThreadDialog } = useDialog(
    CreateWorkOrderThreadDialog,
  );

  function goToThread(threadId: string) {
    navigate({
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id/messages/$threadId",
      params: (prev) => ({ ...prev, id: workOrderId, threadId }),
    });
  }

  function goToThreadList() {
    navigate({
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id/messages",
      params: (prev) => ({ ...prev, id: workOrderId }),
    });
  }

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const { sort, onSortChange } = useThreadSort();
  const { threads, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useThreads({
      supplier_id: supplierId ?? undefined,
      threadable_type: ModelType.WorkOrder,
      threadable_id: workOrderId,
      search: debouncedSearch,
      sort,
    });

  const subscribedUserIds = new Set(
    threads.flatMap((thread) =>
      thread.subscribers.map((subscriber) => subscriber.user_id),
    ),
  );

  const drivers = workOrder.assignments
    .map((assignment) => ({
      user_id: assignment.proDriver.user_id,
      name: assignment.proDriver.name,
    }))
    .filter(
      (driver): driver is { user_id: string; name: string } =>
        driver.user_id !== null && driver.user_id !== undefined,
    );

  const driversWithoutThread = drivers.filter(
    (driver) => !subscribedUserIds.has(driver.user_id),
  );

  function createAllDriversThread() {
    if (drivers.length === 0) {
      toast.error("This work order has no assigned drivers");
      return;
    }

    createThread(
      {
        input: {
          threadable_type: "WORK_ORDER",
          threadable_id: workOrderId,
          user_ids: drivers.map((driver) => driver.user_id),
        },
      },
      {
        onSuccess: (data) => goToThread(data.createThread.id),
        onError: () => toast.error("Failed to create thread"),
      },
    );
  }

  function createDriverThread(driver: { user_id: string }) {
    createThread(
      {
        input: {
          threadable_type: "WORK_ORDER",
          threadable_id: workOrderId,
          user_ids: [driver.user_id],
        },
      },
      {
        onSuccess: (data) => goToThread(data.createThread.id),
        onError: () => toast.error("Failed to create thread"),
      },
    );
  }

  return (
    <ThreadListPanel
      threads={threads}
      isLoading={isLoading}
      headerAction={
        isAdmin ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={isCreatingThread}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                <DropdownMenuItem onSelect={createAllDriversThread}>
                  <TruckIcon className="mr-2 h-4 w-4" />
                  All drivers
                </DropdownMenuItem>
                {driversWithoutThread.length > 0 ? (
                  <DropdownMenuSeparator />
                ) : null}
                {driversWithoutThread.map((driver) => (
                  <DropdownMenuItem
                    key={driver.user_id}
                    onSelect={() => createDriverThread(driver)}
                  >
                    <UserIcon className="mr-2 h-4 w-4" />
                    {driver.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={async () => {
                    try {
                      const result = await openCreateThreadDialog({
                        workOrderId,
                      });
                      goToThread(result.id);
                    } catch {
                      // dialog cancelled
                    }
                  }}
                >
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Other
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null
      }
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      selectedId={selectedId}
      onSelect={(thread) => goToThread(thread.id)}
      onClearSelection={goToThreadList}
      search={search}
      onSearchChange={setSearch}
      sort={sort}
      onSortChange={onSortChange}
    />
  );
}
